// Main search page functionality with Boost AI integration
class MainSearch {
  constructor() {
    this.currentPage = 1;
    this.productsPerPage = 16; // Default, will be read from section settings
    this.currentQuery = "";
    this.currentProductType = "";
    this.currentSort = "";
    this.currentFilters = {}; // Store filter parameters (pf_* only, after conversion)
    this.rawFilters = {}; // Store all raw filter parameters before conversion
    this.priceMin = null; // Store price min filter separately (in cents)
    this.priceMax = null; // Store price max filter separately (in cents)
    this.filterTree = null; // Store Boost AI filter tree for building active facets
    this.boostDisabled = false; // Persisted via sessionStorage between page loads
    this.market = 'AT'
    this.currency = 'EUR'
    this.init();
  }

  init() {
    // Check if we're on a search page
    if (
      window.location.pathname !== "/search" &&
      !window.location.pathname.includes("/search")
    ) {
      return;
    }

    // Read persisted Boost-search disable flag (e.g. if Boost 403'd before)
    try {
      if (window.sessionStorage) {
        this.boostDisabled =
          window.sessionStorage.getItem("boostSearchDisabled") === "1";
      }
    } catch (e) {
      this.boostDisabled = false;
    }

    // Get products per page from section data attribute
    const section = document.querySelector(".template-search");
    if (section) {
      if (section.dataset.productsPerPage) {
        this.productsPerPage = parseInt(section.dataset.productsPerPage) || 16;
      }
      if (section.dataset.shopCurrency) {
        this.currency = section.dataset.shopCurrency || "EUR";
      }
      if (section.dataset.shopMarket) {
        this.market = section.dataset.shopMarket || "AT";
      }
    }

    // Get current search query and page from URL
    const urlParams = new URLSearchParams(window.location.search);
    this.currentQuery = urlParams.get("q") || "";
    this.currentPage = parseInt(urlParams.get("page")) || 1;
    this.currentProductType = urlParams.get("product_type") || "";
    this.currentSort = urlParams.get("sort_by") || "";

    // Extract filter parameters from URL
    this.currentFilters = this.extractFiltersFromURL(urlParams);

    // Intercept sort changes from facet-short
    this.setupSortHandlers();

    // Intercept filter changes from facet-form
    this.setupFilterHandlers();

    // Set up MutationObserver to keep facet-count updated
    this.setupFacetCountObserver();

    // Override FacetCount onFacetUpdate to prevent it from clearing on search pages
    this.overrideFacetCountBehavior();

    // Monitor FacetFiltersActive for unexpected changes
    this.setupActiveFacetsObserver();

    // On search: clicking "remove filter" or "clear all" does a full page load so form state matches
    this.setupFacetRemoveRefresh();

    // Intercept search form submission
    const searchForms = document.querySelectorAll('form[action*="search"]');
    searchForms.forEach((form) => {
      form.addEventListener("submit", (e) => {
        const isSearchPage =
          window.location.pathname === "/search" ||
          window.location.pathname.includes("/search");
        if (isSearchPage) {
          return; // Let form submit normally for full refresh
        }
        e.preventDefault();
        const submittedForm = e.target;
        const formData = new FormData(submittedForm);
        const query = formData.get("q") || "";
        const productType =
          formData.get("product_type") ||
          submittedForm
            .closest("predictive-search")
            ?.querySelector("#SearchProductTypes")?.value ||
          document.getElementById("SearchProductTypes")?.value ||
          "";

        this.currentQuery = query;
        this.currentProductType = productType;
        this.currentPage = 1;
        this.syncSearchInput();
        this.closePredictiveSearchPreviews();
        this.performSearch();
      });
    });

    // Keep search inputs in sync with URL (e.g. when landing with ?q=...)
    this.syncSearchInput();

    // Handle pagination clicks
    this.setupPaginationHandlers();

    // Show loading state if query exists on page load
    if (this.currentQuery && !this.boostDisabled) {
      this.showLoadingState();
      this.performSearch();
    } else {
      // Update filter count on initial load
      this.updateAppliedFilterCount();
    }
  }

  // Sync inputs between predictive and main search to avoid mismatch
  syncSearchInput() {
    const searchInputs = document.querySelectorAll(
      'form[action*="search"] input[name="q"]',
    );
    if (this.currentQuery !== undefined) {
      searchInputs.forEach((input) => {
        input.value = this.currentQuery;
      });
    }
  }

  // Close all predictive-search previews (header + main-search)
  // to prevent overlap with search results when Enter is pressed.
  closePredictiveSearchPreviews() {
    const predictiveSearchElements =
      document.querySelectorAll("predictive-search");
    predictiveSearchElements.forEach((element) => {
      // Remove open state class
      element.classList.remove("predictive-search-open");
      // Hide search content
      const searchContent = element.querySelector(".search__content");
      if (searchContent) {
        searchContent.classList.add("hidden");
      }
    });
    // Remove body class if header search was open
    document.body.classList.remove("search-open");
  }

  showLoadingState() {
    const productsList = document.getElementById("ProductsList");
    if (!productsList) {
      return;
    }

    const hasPlaceholders = productsList.querySelector(
      ".f-column.card[style*='opacity: 0.6']",
    );
    if (hasPlaceholders) {
      return;
    }

    const existingProducts = productsList.querySelectorAll(
      ".f-column.card:not([style*='opacity: 0.6'])",
    );
    existingProducts.forEach((product) => {
      product.style.display = "none";
    });

    const placeholderCount = this.productsPerPage;
    const placeholderHtml = this.createLoadingPlaceholders(placeholderCount);
    productsList.insertAdjacentHTML("afterbegin", placeholderHtml);
  }

  createLoadingPlaceholders(count) {
    const productsList = document.getElementById("ProductsList");
    if (!productsList) {
      return "";
    }

    // Get existing card structure to match styling
    const existingCard = productsList.querySelector(".f-column.card");
    const cardClasses = existingCard ? existingCard.className : "f-column card";
    const productCard = existingCard?.querySelector(".product-card");
    const productCardClasses = productCard
      ? productCard.className
      : "product-card product-card-style-card";

    let placeholdersHtml = "";
    for (let i = 0; i < count; i++) {
      placeholdersHtml += `
        <div class="${cardClasses}" style="opacity: 0.6; pointer-events: none; animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;">
          <div class="${productCardClasses}">
            <div class="product-card__wrapper h-full">
              <div class="product-card__image-wrapper" style="background: rgba(var(--color-border), 0.1); border-radius: var(--pcard-radius); overflow: hidden;">
                <div style="aspect-ratio: 1/1; display: flex; align-items: center; justify-content: center; background: rgba(var(--color-border), 0.05);">
                  <svg class="placeholder-svg" viewBox="0 0 525.5 525.5" style="width: 100%; height: 100%; opacity: 0.2;">
                    <rect width="525.5" height="525.5" fill="currentColor"/>
                  </svg>
                </div>
              </div>
              <div class="product-card__info text-left" style="padding: 1.2rem;">
                <div style="height: 1.5rem; background: rgba(var(--color-border), 0.1); border-radius: 0.4rem; margin-bottom: 0.8rem; width: 80%;"></div>
                <div style="height: 1.5rem; background: rgba(var(--color-border), 0.1); border-radius: 0.4rem; margin-bottom: 1.2rem; width: 60%;"></div>
                <div style="height: 2rem; background: rgba(var(--color-border), 0.1); border-radius: 0.4rem; width: 40%; margin-bottom: 0.4rem;"></div>
                <div style="height: 3rem; background: rgba(var(--color-border), 0.1); border-radius: 0.4rem; margin-top: 1.2rem; width: 100%;"></div>
              </div>
            </div>
          </div>
        </div>
      `;
    }
    return placeholdersHtml;
  }

  removeLoadingState() {
    const productsList = document.getElementById("ProductsList");
    if (!productsList) {
      return;
    }

    const placeholders = productsList.querySelectorAll(
      ".f-column.card[style*='opacity: 0.6']",
    );
    placeholders.forEach((placeholder) => {
      placeholder.remove();
    });

    const hiddenProducts = productsList.querySelectorAll(
      ".f-column.card[style*='display: none']",
    );
    hiddenProducts.forEach((product) => {
      product.remove();
    });
  }

  setupSortHandlers() {
    // Override FacetShort updateFormParams to use Boost AI on search pages
    const overrideSortHandler = () => {
      const sortSelects = document.querySelectorAll("select[is='facet-short']");
      sortSelects.forEach((sortSelect) => {
        if (sortSelect._boostAISortOverridden) {
          return;
        }
        sortSelect._boostAISortOverridden = true;
        const originalUpdateFormParams =
          sortSelect.updateFormParams?.bind(sortSelect);

        sortSelect.updateFormParams = (event) => {
          if (
            window.location.pathname === "/search" ||
            window.location.pathname.includes("/search")
          ) {
            // If Boost is disabled, fall back to original behavior (Shopify default)
            if (this.boostDisabled) {
              if (originalUpdateFormParams) {
                originalUpdateFormParams(event);
              }
              return;
            }
            event.preventDefault();
            event.stopPropagation();
            this.currentSort = event.target.value;
            this.currentPage = 1;
            const url = new URL(window.location.href);
            url.searchParams.set("sort_by", this.currentSort);
            url.searchParams.delete("page");
            window.history.pushState({}, "", url);
            this.updateAppliedFilterCount(url.searchParams);
            this.refreshActiveFacets();
            this.performSearch();
          } else {
            if (originalUpdateFormParams) {
              originalUpdateFormParams(event);
            }
          }
        };
      });
    };

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", overrideSortHandler);
    } else {
      overrideSortHandler();
    }
    const observer = new MutationObserver(overrideSortHandler);
    observer.observe(document.body, { childList: true, subtree: true });
  }

  setupFilterHandlers() {
    // Override FacetForm renderSection to use Boost AI on search pages
    const overrideFilterHandler = () => {
      const facetForms = document.querySelectorAll("form[is='facet-form']");
      facetForms.forEach((form) => {
        if (form._boostAIFilterOverridden) {
          return;
        }
        form._boostAIFilterOverridden = true;
        const originalRenderSection = form.renderSection?.bind(form);

        form.renderSection = async (url, event) => {
          if (
            window.location.pathname === "/search" ||
            window.location.pathname.includes("/search")
          ) {
            // If Boost is disabled, use the original FacetForm behavior
            if (this.boostDisabled) {
              if (originalRenderSection) {
                return originalRenderSection(url, event);
              }
              return;
            }
            const urlStr = typeof url === "string" ? url : url.toString();
            const urlObj = new URL(urlStr, window.location.origin);
            const urlParams = new URLSearchParams(urlObj.search);

            this.currentFilters = this.extractFiltersFromURL(urlParams);
            this.currentQuery = urlParams.get("q") || this.currentQuery;
            this.currentProductType =
              urlParams.get("product_type") || this.currentProductType;
            // Keep current sort when applying filters – facet form often sends default "relevance"
            const urlSort = urlParams.get("sort_by");
            if (urlSort && urlSort !== "relevance") {
              this.currentSort = urlSort;
            }
            this.currentPage = 1;

            // Build URL with all params from form (preserving multiple values)
            const currentUrl = new URL(
              window.location.origin + window.location.pathname,
            );
            urlParams.forEach((value, key) => {
              if (key !== "section_id" && key !== "page" && key !== "sort_by") {
                // Append all values for this key (handles duplicates)
                urlParams.getAll(key).forEach((val) => {
                  currentUrl.searchParams.append(key, val);
                });
              }
            });
            if (this.currentSort) {
              currentUrl.searchParams.set("sort_by", this.currentSort);
            }

            window.history.pushState({}, "", currentUrl);
            this.updateAppliedFilterCount(currentUrl.searchParams);

            // Use the updated URL for active facets
            this.refreshActiveFacets(currentUrl);

            if (form.hasAttribute("data-history")) {
              const clonedUrl = new URL(urlStr, window.location.origin);
              clonedUrl.searchParams.delete("section_id");
              history.replaceState({}, "", clonedUrl.toString());
            }
            form.dirty = false;
            this.showLoadingState();
            this.performSearch();
          } else {
            if (originalRenderSection) {
              originalRenderSection(url, event);
            }
          }
        };

        // Also override renderProductCount directly to prevent it from clearing our count
        const originalRenderProductCount = form.renderProductCount?.bind(form);
        form.renderProductCount = (responseText) => {
          if (
            window.location.pathname === "/search" ||
            window.location.pathname.includes("/search")
          ) {
            // Don't update ProductCount on search pages - Boost AI handles it
            return;
          } else {
            if (originalRenderProductCount) {
              originalRenderProductCount(responseText);
            }
          }
        };
      });
    };

    overrideFilterHandler();
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", overrideFilterHandler);
    }
    const observer = new MutationObserver(overrideFilterHandler);
    observer.observe(document.body, { childList: true, subtree: true });
  }

  setupPaginationHandlers() {
    // Handle pagination link clicks
    document.addEventListener("click", (e) => {
      const paginationLink = e.target.closest(".pagination__item[href]");
      if (paginationLink && paginationLink.href) {
        const url = new URL(paginationLink.href);
        if (url.pathname.includes("/search")) {
          e.preventDefault();
          const page = parseInt(url.searchParams.get("page")) || 1;
          const query = url.searchParams.get("q") || this.currentQuery;
          const productType =
            url.searchParams.get("product_type") || this.currentProductType;
          const sort = url.searchParams.get("sort_by") || this.currentSort;
          const filters = this.extractFiltersFromURL(url.searchParams);

          this.currentPage = page;
          this.currentQuery = query;
          this.currentProductType = productType;
          this.currentSort = sort;
          this.currentFilters = filters;
          this.scrollToResultsTop();
          // If Boost is disabled, just navigate with a full page reload
          if (this.boostDisabled) {
            window.location.href = url.toString();
            return;
          }
          this.performSearch();
        }
      }

      // Handle load-more button
      const loadMoreBtn = e.target.closest("button[is='load-more-button']");
      if (loadMoreBtn) {
        e.preventDefault();
        this.currentPage += 1;
        this.loadMoreProducts();
      }
    });
  }

  // Scroll the results grid into view, matching the collection page's
  // pagination behavior (see facets.js beforeRenderSection).
  scrollToResultsTop() {
    const target = document.querySelector(".collection");
    if (!target) return;
    window.scrollTo({
      top: target.getBoundingClientRect().top + window.scrollY - 200,
      behavior:
        typeof FoxTheme !== "undefined" && FoxTheme.config?.motionReduced
          ? "auto"
          : "smooth",
    });
  }

  /**
   * On search, facet-remove clicks (remove one filter or "clear all") do a full
   * page load so the server-rendered form matches the URL.
   */
  setupFacetRemoveRefresh() {
    document.addEventListener(
      "click",
      (e) => {
        if (
          window.location.pathname !== "/search" &&
          !window.location.pathname.includes("/search")
        ) {
          return;
        }
        const link = e.target.closest('a[is="facet-remove"]');
        if (!link || !link.href) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        window.location.href = link.href;
      },
      true,
    );
  }

  extractFiltersFromURL(urlParams) {
    const filters = {};
    this.rawFilters = {}; // Store all filter parameters for conversion

    // Get all unique parameter keys (to handle multiple values per key)
    const allKeys = new Set();
    urlParams.forEach((value, key) => {
      allKeys.add(key);
    });

    // Extract all filter parameters (pf_*, filter.p.m.custom.*, filter.v.price.*)
    allKeys.forEach((key) => {
      // Skip non-filter parameters
      if (
        key === "q" ||
        key === "page" ||
        key === "sort_by" ||
        key === "product_type" ||
        key === "section_id"
      ) {
        return;
      }

      // Get ALL values for this key (e.g. multiple values for pf_v_content)
      const allValues = urlParams.getAll(key);

      // Normalize key: remove [] suffix if present (pf_v_content[] -> pf_v_content)
      const normalizedKey = key.endsWith("[]") ? key.slice(0, -2) : key;

      // Store all filter parameters in rawFilters for conversion
      if (
        normalizedKey.startsWith("pf_") ||
        normalizedKey.startsWith("filter.")
      ) {
        if (allValues.length > 1) {
          this.rawFilters[normalizedKey] = allValues;
        } else {
          this.rawFilters[normalizedKey] = allValues[0];
        }
      }

      // Extract pf_* parameters directly (already in Boost AI format)
      if (normalizedKey.startsWith("pf_")) {
        if (allValues.length > 1) {
          filters[normalizedKey] = allValues;
        } else {
          filters[normalizedKey] = allValues[0];
        }
      }
    });

    // Extract price range filters - store separately for conversion
    const minPrice = urlParams.get("filter.v.price.gte");
    const maxPrice = urlParams.get("filter.v.price.lte");
    if (minPrice) {
      this.priceMin = parseInt(minPrice, 10);
    } else {
      this.priceMin = null;
    }
    if (maxPrice) {
      this.priceMax = parseInt(maxPrice, 10);
    } else {
      this.priceMax = null;
    }

    return filters;
  }

  setupBoostAIURL(page = 1) {
    // Get Shopify store domain
    let shopDomain = window.Shopify.shop;
    if (shopDomain) {
      shopDomain = shopDomain.replace(/^https?:\/\//, "").replace(/\/$/, "");
    } else {
      return;
    }

    const url = new URL("https://services.mybcapps.com/bc-sf-filter/search");
    url.searchParams.set("shop", shopDomain);
    url.searchParams.set("q", this.currentQuery);

    if (this.currentProductType) {
      url.searchParams.set("product_type", this.currentProductType);
    }

    // Add sort parameter if set (Boost AI accepts array format)
    if (this.currentSort) {
      url.searchParams.set("sort", this.currentSort);
    }

    // Add filter parameters - only pass pf_* parameters (Boost AI format)
    // Boost AI expects array syntax for filters: pf_v_content[]=value (even for single values)
    Object.keys(this.currentFilters).forEach((key) => {
      if (key.startsWith("filter.v.price.")) {
        return;
      }

      const value = this.currentFilters[key];
      if (Array.isArray(value)) {
        // Multiple values: append each with array syntax
        value.forEach((v) => {
          url.searchParams.append(`${key}[]`, v);
        });
      } else {
        // Single value: also use array syntax for consistency with Boost AI
        url.searchParams.append(`${key}[]`, value);
      }
    });

    url.searchParams.set("limit", this.productsPerPage.toString());
    url.searchParams.set("page", page.toString());
    url.searchParams.set("event_type", "filter");
    url.searchParams.set("product_available", "0");
    url.searchParams.set("currency", this.currency);
    url.searchParams.set("country", this.market);
    // Always request filter tree so we get doc_count for filter option counts (also on initial load)
    url.searchParams.set("build_filter_tree", "1");
    return url;
  }

  setFacetLoading(isLoading) {
    const els = document.querySelectorAll("[data-facet-loading]");
    els.forEach((el) => {
      if (isLoading) {
        el.classList.add("btn--loading");
      } else {
        el.classList.remove("btn--loading");
      }

      // Never hide the whole container (it may contain ProductCount).
      if (el instanceof HTMLElement) {
        el.style.display = "";
      }

      const spinner = el.querySelector(".loading__spinner");
      if (spinner) {
        spinner.classList.toggle("hidden", !isLoading);
      }
    });
  }

  async performSearch() {
    const productsList = document.getElementById("ProductsList");
    if (!productsList) {
      return;
    }

    // If Boost search is disabled for this session/shop,
    // just fall back to the native Shopify search behavior.
    if (this.boostDisabled) {
      this.submitForm();
      return;
    }

    const originalClasses = productsList.className;
    const originalStyle = productsList.getAttribute("style") || "";
    const originalDataAttrs = {
      gridClass: productsList.getAttribute("data-grid-class"),
      listClass: productsList.getAttribute("data-list-class"),
    };

    const hasPlaceholders = productsList.querySelector(
      ".f-column.card[style*='opacity: 0.6']",
    );
    if (!hasPlaceholders) {
      productsList.style.opacity = "0.5";
    } else {
      productsList.style.opacity = "1";
    }
    this.setFacetLoading(true);
    this.clearFilterOptionCounts();

    try {
      const boostAIUrl = this.setupBoostAIURL(this.currentPage);
      const response = await fetch(boostAIUrl);

      if (!response.ok) {
        this.removeLoadingState();
        const productsList = document.getElementById("ProductsList");
        if (productsList) {
          productsList.style.opacity = "1";
        }
        this.setFacetLoading(false);
        // Persist that Boost failed so subsequent loads fall back to native search
        try {
          if (window.sessionStorage) {
            window.sessionStorage.setItem("boostSearchDisabled", "1");
          }
        } catch (e) { }
        this.boostDisabled = true;
        this.submitForm();
        return;
      }

      const boostData = await response.json();

      // Store filter tree for building active facets
      if (boostData.filter) {
        this.filterTree = boostData.filter;
      }

      // Convert filters using filter tree
      const hasRawFilters = Object.keys(this.rawFilters).length > 0;
      const hasPriceFilters = this.priceMin !== null || this.priceMax !== null;
      let needsConversion = false;
      const filterOptions = boostData.filter?.options || [];

      if ((hasRawFilters || hasPriceFilters) && filterOptions.length > 0) {
        // Convert price filters: pf_p_price[]=min:max
        if (hasPriceFilters) {
          const priceFilterOption = filterOptions.find(
            (opt) =>
              opt.filterType === "price" ||
              opt.filterOptionId === "pf_p_price" ||
              opt.label?.toLowerCase().includes("price"),
          );

          if (priceFilterOption && priceFilterOption.filterOptionId) {
            const priceFilterId = priceFilterOption.filterOptionId;
            let priceValue = null;
            if (this.priceMin !== null && this.priceMax !== null) {
              priceValue = `${this.priceMin.toFixed(2)}:${this.priceMax.toFixed(2)}`;
            } else if (this.priceMin !== null) {
              priceValue = `${this.priceMin.toFixed(2)}:`;
            } else if (this.priceMax !== null) {
              priceValue = `:${this.priceMax.toFixed(2)}`;
            }

            if (priceValue) {
              this.currentFilters[priceFilterId] = [priceValue];
              needsConversion = true;
            }
          }
        }

        // Convert filter.p.m.custom.* to pf_m_::custom::* format (Boost AI pf_*)
        // URL can have multiple values: filter.p.m.custom.content=0,7l&filter.p.m.custom.content=1
        Object.keys(this.rawFilters).forEach((key) => {
          if (key.startsWith("filter.v.price.")) {
            return;
          }

          if (
            key.startsWith("filter.p.m.custom.") ||
            key.startsWith("filter.p.")
          ) {
            const rawValueOrArray = this.rawFilters[key];
            const rawValues = Array.isArray(rawValueOrArray)
              ? rawValueOrArray
              : [rawValueOrArray];

            let customFieldName = null;
            if (key.startsWith("filter.p.m.custom.")) {
              customFieldName = key.replace("filter.p.m.custom.", "");
            } else if (key.startsWith("filter.p.")) {
              customFieldName = key.replace("filter.p.", "");
            }

            // Find filter option by field name (not by value)
            for (const filterOption of filterOptions) {
              if (
                customFieldName &&
                filterOption.filterType ===
                `metafield_::custom::${customFieldName}`
              ) {
                if (filterOption.values && Array.isArray(filterOption.values)) {
                  // Map each URL value to Boost AI value key (handles multiple values)
                  const convertedValues = rawValues
                    .map((v) => {
                      const decoded = decodeURIComponent(String(v));
                      const match = filterOption.values.find(
                        (val) =>
                          typeof val === "object" &&
                          val.key !== undefined &&
                          (String(val.key) === decoded ||
                            String(val.key) === v),
                      );
                      return match ? match.key : decoded;
                    })
                    .filter(Boolean);

                  if (convertedValues.length > 0) {
                    const filterId = filterOption.filterOptionId;
                    this.currentFilters[filterId] =
                      convertedValues.length === 1
                        ? convertedValues[0]
                        : convertedValues;
                    needsConversion = true;
                    break;
                  }
                }
              }
            }
          }
        });

        // Make another request with converted filters
        if (needsConversion) {
          const convertedUrl = this.setupBoostAIURL(this.currentPage);
          const convertedResponse = await fetch(convertedUrl);
          if (convertedResponse.ok) {
            const convertedData = await convertedResponse.json();
            const convertedHandles = (convertedData.products || [])
              .map((p) => p.handle)
              .filter(Boolean);

            if (convertedHandles.length > 0) {
              // Only use cards from #ProductsList (actual results), not from predictive-search preview
              const productsList = document.getElementById("ProductsList");
              const existingCard =
                productsList?.querySelector(
                  ".product-card.product-card-style-card",
                ) ||
                document.querySelector(".product-card.product-card-style-card");
              let pcardColorScheme = "scheme-1";
              let shouldUseColorInherit = false;
              if (existingCard) {
                const colorClass = Array.from(existingCard.classList).find(
                  (c) =>
                    c.startsWith("color-") &&
                    !c.includes("inherit") &&
                    c !== "color-inherit",
                );
                if (colorClass) {
                  // Extract scheme: color-scheme-2 -> scheme-2, color-2 -> scheme-2
                  if (colorClass.startsWith("color-scheme-")) {
                    pcardColorScheme = colorClass.replace("color-", "");
                  } else {
                    pcardColorScheme = colorClass.replace("color-", "scheme-");
                  }
                }
                shouldUseColorInherit =
                  existingCard.classList.contains("color-inherit");
              }

              const productCards = await this.fetchProductCards(
                convertedHandles,
                convertedData.products,
                pcardColorScheme,
                shouldUseColorInherit,
              );

              this.removeLoadingState();
              this.renderProducts(productCards, convertedData, {
                originalClasses: productsList.className,
                originalStyle: productsList.getAttribute("style") || "",
                originalDataAttrs: {
                  gridClass: productsList.getAttribute("data-grid-class"),
                  listClass: productsList.getAttribute("data-list-class"),
                },
              });
              this.updateURL();
              this.syncSearchInput();
              this.updateProductCount(convertedData.total_product);
              this.updateAppliedFilterCount();
              this.updateFilterOptionCountsFromBoostAI(convertedData.filter);
              return;
            }

            // Converted request returned 0 products: show empty state, keep filter bar visible
            this.removeLoadingState();
            const productsListForEmpty = document.getElementById("ProductsList");
            if (productsListForEmpty) {
              productsListForEmpty.style.opacity = "1";
            }
            this.setFacetLoading(false);
            if (convertedData.filter) {
              this.filterTree = convertedData.filter;
            }
            this.updateURL();
            this.updateProductCount(0);
            this.updateAppliedFilterCount();
            this.updateFilterOptionCountsFromBoostAI(convertedData.filter);
            this.updatePagination({ total_product: 0 });
            this.setToolbarAndFiltersVisibility(true);
            this.refreshActiveFacets();
            this.renderEmptyResults();
            return;
          }
        }
      }

      let products = boostData.products || [];

      // Client-side filter for products without price min/max field Boost-AI uses
      if (
        this.currentSort === "price-ascending" ||
        this.currentSort === "price-descending"
      ) {
        products = [...products].filter(
          (p) => p.price_min > 0 || p.price_max > 0,
        );
      }

      const productHandles = products.map((p) => p.handle).filter(Boolean);

      if (productHandles.length === 0) {
        this.removeLoadingState();
        const productsList = document.getElementById("ProductsList");
        if (productsList) {
          productsList.style.opacity = "1";
        }
        this.setFacetLoading(false);
        this.updateProductCount(0);
        this.updateAppliedFilterCount();
        this.updateFilterOptionCountsFromBoostAI(boostData.filter);
        this.updatePagination({ total_product: 0 });
        this.setToolbarAndFiltersVisibility(true);
        this.refreshActiveFacets();
        this.renderEmptyResults();
        return;
      }

      // Only use cards from #ProductsList (actual results),
      // not from predictive-search preview (header or main-search)
      const productsList = document.getElementById("ProductsList");
      const existingCard =
        productsList?.querySelector(".product-card.product-card-style-card") ||
        document.querySelector(".product-card.product-card-style-card");
      let pcardColorScheme = "scheme-1";
      let shouldUseColorInherit = false;
      if (existingCard) {
        const colorClass = Array.from(existingCard.classList).find(
          (c) =>
            c.startsWith("color-") &&
            !c.includes("inherit") &&
            c !== "color-inherit",
        );
        if (colorClass) {
          pcardColorScheme = colorClass.replace("color-", "");
        }
        shouldUseColorInherit =
          existingCard.classList.contains("color-inherit");
      }

      const productCards = await this.fetchProductCards(
        productHandles,
        products,
        pcardColorScheme,
        shouldUseColorInherit,
      );

      this.removeLoadingState();

      this.renderProducts(productCards, boostData, {
        originalClasses,
        originalStyle,
        originalDataAttrs,
      });

      // Update URL without reload
      this.updateURL();
      this.syncSearchInput();

      // Update product count
      this.updateProductCount(boostData.total_product);
      this.updateAppliedFilterCount();

      // Update filter option counts from Boost AI (not Shopify's)
      this.updateFilterOptionCountsFromBoostAI(boostData.filter);

      // Refresh active facets (will use Shopify response if available, otherwise build manually)
      this.refreshActiveFacets();
    } catch (error) {
      // Fallback to default form submission
      try {
        if (window.sessionStorage) {
          window.sessionStorage.setItem("boostSearchDisabled", "1");
        }
      } catch (e) { }
      this.boostDisabled = true;
      this.submitForm();
    } finally {
      productsList.style.opacity = "1";
      this.setFacetLoading(false);
    }
  }

  async fetchProductCards(
    productHandles,
    boostProducts,
    pcardColorScheme,
    useColorInherit = false,
  ) {
    const templateSearch = document.querySelector(".template-search");
    const showSaleBadge =
      templateSearch?.dataset.pcardShowSaleBadge !== "false" &&
      templateSearch?.dataset.pcardShowSaleBadge !== "";
    const saleBadgeType =
      templateSearch?.dataset.pcardSaleBadgeType || "show_text";
    const showSecondImage =
      templateSearch?.dataset.pcardShowSecondImg !== "false";
    const saleBadgeCrazyWeeks =
      templateSearch?.dataset.pcardSaleBadgeCrazyWeeks === "true";
    const saleBadgeSettings = {
      showSaleBadge,
      saleBadgeType,
      saleBadgeCrazyWeeks,
    };

    const productCardPromises = productHandles.map(async (handle, index) => {
      try {
        const product = boostProducts[index];
        if (!product) {
          return null;
        }

        const productUrl = `${window.shopUrl}/products/${handle}`;
        const response = await fetch(productUrl);
        if (!response.ok) {
          return null;
        }

        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");

        // Extract the canonical PDP price block first:
        // product-information-blocks.liquid => #price-{{ section.id }}[role="status"]
        const priceElement =
          doc.querySelector(
            '.section-main-product [id^="price-"][role="status"] .f-price',
          ) ||
          doc.querySelector(".product-card .f-price") ||
          doc.querySelector(".section-main-product .f-price") ||
          doc.querySelector(".product__info-wrapper .f-price") ||
          doc.querySelector(".f-price");

        const priceHtml = priceElement
          ? priceElement.outerHTML
          : '<div class="f-price">...</div>';
        const ribbonInfo = SearchUtils.getProductRibbonInfo(doc);

        if (product?.price && product.price > 0) {
          const priceItem = priceElement.querySelector('.f-price-item')
          if (priceItem) {
            priceItem.innerHTML = product.price
          }
        }
        if (product?.compare_at_price && product.compare_at_price > 0) {
          const compareToItem = priceElement.querySelector('.f-price-item--strikethrough');
          if (compareToItem) {
            compareToItem.innerHTML = product.compare_at_price;
          }
        }

        let firstImage = "";
        let secondImage = "";

        if (product.images_info && product.images_info.length > 0) {
          firstImage = product.images_info[0].src || "";
        }
        if (
          showSecondImage &&
          product.images_info &&
          product.images_info.length > 1
        ) {
          secondImage = product.images_info[1].src || "";
        }

        if (!firstImage) {
          const productImage = doc.querySelector(
            '.product__media img, .product-card__image img, img[src*="/products/"]',
          );
          if (productImage) {
            firstImage = productImage.getAttribute("src") || "";
          }
        }

        // Get card style from existing cards or ProductsList classes
        const productsList = document.getElementById("ProductsList");
        let pcardStyle = "card";
        if (productsList) {
          const hasListClass = productsList.classList
            .toString()
            .includes("products-list--list-");
          if (hasListClass) {
            const listStyleMatch = productsList.className.match(
              /products-list--list-(\w+)/,
            );
            if (listStyleMatch) {
              pcardStyle = listStyleMatch[1];
            } else {
              pcardStyle = "list";
            }
          } else {
            // Only use cards from #ProductsList, not from predictive-search preview
            const existingCard =
              productsList?.querySelector(".product-card") ||
              document.querySelector(".product-card");
            if (existingCard) {
              const styleMatch = existingCard.className.match(
                /product-card-style-(\w+)/,
              );
              if (styleMatch) {
                pcardStyle = styleMatch[1];
              }
            }
          }
        }

        let cardHtml = '<div class="f-column card">';
        cardHtml +=
          '<div class="product-card product-card-style-' +
          pcardStyle +
          (pcardStyle === "card" ? " color-" + pcardColorScheme : "") +
          (useColorInherit ? " color-inherit" : "") +
          '" data-product-handle="' +
          SearchUtils.escapeHtml(handle) +
          '">';
        cardHtml += '<div class="product-card__wrapper h-full">';

        const overlayColorScheme = pcardColorScheme;
        cardHtml +=
          '<div class="product-card__image-wrapper' +
          (secondImage ? "" : " product-card__image-wrapper--main-only") +
          " color-" +
          overlayColorScheme +
          ' bg-none">';
        cardHtml += '<div class="product-card__wishlist-badge">';
        cardHtml +=
          '<div class="wishlist-collection-button"><button type="button" aria-label="Add to Wishlist" class="wkh-button wkh-align-center wkh-align-content-center" data-product-handle="' +
          SearchUtils.escapeHtml(handle) +
          '"><div class="wkh-icon" icon="wishlist"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="21" viewBox="0 0 24 21" class="heart-icon" aria-hidden="true" style="--heart-fill: url(#heartGradient);"><defs><linearGradient id="heartGradient" x1="3.8" y1="0" x2="21.6" y2="1.7" gradientUnits="userSpaceOnUse"><stop stop-color="#DD0044"/><stop offset="1" stop-color="#FF5533"/></linearGradient></defs><path class="heart-path" d="M21.7315 2.05512C20.5096 0.729882 18.833 0 17.0102 0C15.6477 0 14.3999 0.430762 13.3014 1.28022C12.7471 1.709 12.2448 2.23359 11.802 2.84587C11.3594 2.23377 10.8569 1.709 10.3024 1.28022C9.20411 0.430762 7.95631 0 6.59379 0C4.77098 0 3.09421 0.729882 1.87234 2.05512C0.665052 3.36488 0 5.1542 0 7.0937C0 9.08994 0.743929 10.9173 2.3411 12.8445C3.76989 14.5685 5.82339 16.3185 8.2014 18.345C9.0134 19.0371 9.93381 19.8215 10.8895 20.6571C11.142 20.8783 11.466 21 11.802 21C12.1379 21 12.462 20.8783 12.7141 20.6575C13.6698 19.8217 14.5908 19.0369 15.4032 18.3445C17.7808 16.3183 19.8343 14.5685 21.2631 12.8443C22.8603 10.9173 23.604 9.08994 23.604 7.09352C23.604 5.1542 22.939 3.36488 21.7315 2.05512Z" stroke="url(#heartGradient)" stroke-width="1"/></svg></div></button></div>';
        cardHtml += "</div>";
        cardHtml +=
          '<a href="' +
          productUrl +
          '" aria-label="' +
          SearchUtils.escapeHtml(product.title) +
          '">';
        cardHtml += '<motion-element data-motion="zoom-out-sm" class="block">';

        if (firstImage) {
          cardHtml +=
            '<div class="media-wrapper product-card__image product-card__image--main" style="--aspect-ratio: 1">';
          cardHtml +=
            '<img src="' +
            firstImage +
            '" alt="' +
            SearchUtils.escapeHtml(product.title) +
            '" loading="lazy" class="motion-reduce" is="image-lazy">';
          cardHtml += "</div>";

          if (secondImage) {
            cardHtml +=
              '<div class="media-wrapper product-card__image product-card__image--second inset-full hidden md:block" style="--aspect-ratio: 1">';
            cardHtml +=
              '<img src="' +
              secondImage +
              '" alt="' +
              SearchUtils.escapeHtml(product.title) +
              '" loading="lazy" class="motion-reduce" is="image-lazy">';
            cardHtml += "</div>";
          }
        } else {
          cardHtml +=
            '<div class="product-card__image product-card__image--main" style="--aspect-ratio: 1;">';
          cardHtml +=
            '<svg class="placeholder-svg" viewBox="0 0 525.5 525.5"><rect width="525.5" height="525.5" fill="#f0f0f0"/></svg>';
          cardHtml += "</div>";
        }

        cardHtml += "</motion-element></a>";
        const ribbonBadgeHtml = SearchUtils.getRibbonBadgeHtml(ribbonInfo);
        if (ribbonBadgeHtml) {
          cardHtml += ribbonBadgeHtml;
        }
        cardHtml += "</div>";

        const saleBadgeHtml = SearchUtils.getSaleBadgeHtml(
          product,
          saleBadgeSettings,
        );
        if (saleBadgeHtml) {
          cardHtml += saleBadgeHtml;
        }

        // Info section
        cardHtml += '<div class="product-card__info text-left">';
        cardHtml +=
          '<div class="jdgm-widget jdgm-preview-badge" data-id="' +
          product.id +
          '" data-auto-install="false"></div>';
        const showVendor =
          document.querySelector(".template-search")?.dataset.pcardShowVendor !==
          "false";
        if (showVendor && product.vendor) {
          const brandLink = SearchUtils.getBrandLinkFromDocument(doc, product.vendor);
          cardHtml +=
            '<a href="' +
            SearchUtils.escapeHtml(brandLink.url) +
            '" class="text-body reversed-link" aria-label="' +
            SearchUtils.escapeHtml(
              (typeof FoxTheme !== "undefined" &&
                FoxTheme.accessibility?.vendor) ||
              "Vendor ",
            ) +
            SearchUtils.escapeHtml(product.vendor) +
            '"><span class="reversed-link__text">' +
            SearchUtils.escapeHtml(product.vendor) +
            "</span></a>";
        }
        cardHtml += '<p class="product-card__title text-pcard-title">';
        cardHtml +=
          '<a class="reversed-link block text-limit-2-lines" href="' +
          productUrl +
          '">';
        cardHtml +=
          '<span class="reversed-link__text">' +
          SearchUtils.escapeHtml(product.title) +
          "</span>";
        cardHtml += "</a></p>";
        cardHtml += priceHtml;

        // Add to cart button
        let variantId = null;
        let isAvailable = false;

        if (product.variants && product.variants.length > 0) {
          const availableVariant =
            product.variants.find((v) => v.available === true) ||
            product.variants[0];
          variantId = availableVariant.id;
          isAvailable = availableVariant.available === true;
        }

        cardHtml +=
          '<div class="product-card__grid-actions product-card__grid-only">';

        if (variantId) {
          const sectionId = this.getSectionId();
          cardHtml +=
            '<div class="product-form" data-hide-errors="false" data-section-id="' +
            sectionId +
            '">';
          cardHtml +=
            '<form method="post" action="/cart/add" accept-charset="UTF-8" class="main-product-form grid gap-4" enctype="multipart/form-data" data-type="add-to-cart-form" is="product-form" data-hide-errors="false">';
          cardHtml += '<input type="hidden" name="form_type" value="product">';
          cardHtml += '<input type="hidden" name="utf8" value="✓">';
          cardHtml +=
            '<input type="hidden" name="id" value="' +
            variantId +
            '" class="product-variant-id">';
          cardHtml +=
            '<div class="product-form__buttons flex flex-wrap items-end gap-x-2 gap-y-4">';
          cardHtml +=
            '<button type="submit" name="add" class="product-form__submit btn btn--primary"' +
            (isAvailable ? "" : " disabled") +
            ">";
          cardHtml += '<span class="btn__icon-cart">';
          cardHtml += isAvailable
            ? '<svg viewBox="0 0 20 20" fill="none" class="icon icon-cart icon--medium icon--thick" xmlns="http://www.w3.org/2000/svg"><path d="M6.875 18.125C7.56536 18.125 8.125 17.5654 8.125 16.875C8.125 16.1846 7.56536 15.625 6.875 15.625C6.18464 15.625 5.625 16.1846 5.625 16.875C5.625 17.5654 6.18464 18.125 6.875 18.125Z" fill="currentColor"/><path d="M15 18.125C15.6904 18.125 16.25 17.5654 16.25 16.875C16.25 16.1846 15.6904 15.625 15 15.625C14.3096 15.625 13.75 16.1846 13.75 16.875C13.75 17.5654 14.3096 18.125 15 18.125Z" fill="currentColor"/><path d="M1.25 2.5H3.125L5.99609 12.8344C6.06916 13.0976 6.22643 13.3296 6.44384 13.4949C6.66126 13.6603 6.92685 13.7499 7.2 13.75H14.9219C15.1952 13.7501 15.4609 13.6605 15.6785 13.4952C15.8961 13.3298 16.0535 13.0977 16.1266 12.8344L18.125 5.625H3.99297" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>'
            : '<svg xmlns="http://www.w3.org/2000/svg" class="icon icon-bell icon--medium icon--thick" viewBox="0 0 24 24" fill="none"><path d="M6.44784 8.96942C6.76219 6.14032 9.15349 4 12 4C14.8465 4 17.2378 6.14032 17.5522 8.96942L17.804 11.2356C17.8072 11.2645 17.8088 11.279 17.8104 11.2933C17.9394 12.4169 18.3051 13.5005 18.8836 14.4725C18.8909 14.4849 18.8984 14.4973 18.9133 14.5222L19.4914 15.4856C20.0159 16.3599 20.2782 16.797 20.2216 17.1559C20.1839 17.3946 20.061 17.6117 19.8757 17.7668C19.5971 18 19.0873 18 18.0678 18H5.93223C4.91268 18 4.40291 18 4.12434 17.7668C3.93897 17.6117 3.81609 17.3946 3.77841 17.1559C3.72179 16.797 3.98407 16.3599 4.50862 15.4856L5.08665 14.5222C5.10161 14.4973 5.10909 14.4849 5.11644 14.4725C5.69488 13.5005 6.06064 12.4169 6.18959 11.2933C6.19123 11.279 6.19283 11.2645 6.19604 11.2356L6.44784 8.96942Z" stroke="currentColor"/><path d="M9.10222 18.4059C9.27315 19.1501 9.64978 19.8077 10.1737 20.2767C10.6976 20.7458 11.3396 21 12 21C12.6604 21 13.3024 20.7458 13.8263 20.2767C14.3502 19.8077 14.7269 19.1501 14.8978 18.4059" stroke="currentColor" stroke-linecap="round"/></svg>';
          cardHtml += "</span>";
          cardHtml +=
            '<span class="btn__text-card">' +
            (isAvailable
              ? FoxTheme.variantStrings?.addToCart // In den Warenkorb
              : FoxTheme.variantStrings?.soldOut) + // Ausverkauft
            "</span>";
          cardHtml += '<div class="loading__spinner hidden">';
          cardHtml +=
            '<svg aria-hidden="true" focusable="false" class="spinner icon icon--extra-medium" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">';
          cardHtml +=
            '<circle class="path" fill="none" stroke-width="4" cx="33" cy="33" r="30"></circle>';
          cardHtml += "</svg>";
          cardHtml += "</div>";
          cardHtml += "</button>";
          cardHtml += "</div>";
          cardHtml +=
            '<input type="hidden" name="product-id" value="' +
            product.id +
            '">';
          cardHtml +=
            '<input type="hidden" name="section-id" value="' + sectionId + '">';
          cardHtml += "</form>";
          cardHtml += "</div>";
        } else {
          cardHtml +=
            '<a href="' + productUrl + '" class="btn btn--primary btn--small">';
          cardHtml +=
            '<span class="btn__text">' +
            FoxTheme.variantStrings?.addToCart + // In den Warenkorb
            "</span>";
          cardHtml += "</a>";
        }

        cardHtml += "</div>";
        cardHtml += "</div>";

        // List view actions (sibling of product-card__info)
        cardHtml +=
          '<div class="product-card__list-actions hidden gap-3 flex-col">';

        if (variantId) {
          const sectionId = this.getSectionId();
          const productFormId = `list-view-quick-add-${sectionId}-${product.id}`;

          // Get oseid from existing forms
          const existingForm = document.querySelector(
            'form[action*="/cart/add"]',
          );
          let oseidParam = "";
          if (existingForm) {
            const formAction = existingForm.getAttribute("action");
            const oseidMatch = formAction?.match(/[?&]oseid=([^&]*)/);
            if (oseidMatch) {
              oseidParam = `?oseid=${oseidMatch[1]}`;
            }
          }

          cardHtml +=
            '<form method="post" action="/cart/add' +
            oseidParam +
            '" id="' +
            productFormId +
            '" accept-charset="UTF-8" class="product-card__list-actions-main" enctype="multipart/form-data" novalidate="novalidate" data-type="add-to-cart-form" is="product-form">';
          cardHtml += '<input type="hidden" name="form_type" value="product">';
          cardHtml += '<input type="hidden" name="utf8" value="✓">';
          cardHtml +=
            '<input type="hidden" name="id" value="' +
            variantId +
            '" class="product-variant-id"' +
            (!isAvailable ? " disabled" : "") +
            ">";
          cardHtml +=
            '<button id="' +
            productFormId +
            '-submit" type="submit" name="add" aria-label="' +
            FoxTheme.variantStrings?.addToCart + // In den Warenkorb
            '" class="product-card__list-atc btn btn--primary w-full"' +
            (!isAvailable ? " disabled" : "") +
            ">";
          cardHtml +=
            '<span class="btn__text flex gap-1 items-center">' +
            (isAvailable
              ? FoxTheme.variantStrings?.addToCart // In den Warenkorb
              : FoxTheme.variantStrings?.soldOut) + // Ausverkauft
            "</span>";
          cardHtml += '<div class="loading__spinner hidden">';
          cardHtml +=
            '<svg aria-hidden="true" focusable="false" class="spinner icon icon--extra-medium" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">';
          cardHtml +=
            '<circle class="path" fill="none" stroke-width="4" cx="33" cy="33" r="30"></circle>';
          cardHtml += "</svg>";
          cardHtml += "</div>";
          cardHtml += "</button>";
          cardHtml +=
            '<input type="hidden" name="product-id" value="' +
            product.id +
            '">';
          cardHtml +=
            '<input type="hidden" name="section-id" value="' + sectionId + '">';
          if (oseidParam) {
            const oseidValue = oseidParam.replace("?oseid=", "");
            cardHtml +=
              '<input type="hidden" name="oseid" value="' + oseidValue + '">';
          }
          cardHtml += "</form>";
        }

        // Details button
        cardHtml += '<a href="' + productUrl + '" class="btn btn--secondary">';
        cardHtml +=
          '<span class="btn__text flex gap-1 items-center">' +
          FoxTheme.variantStrings?.viewDetails + // Details anzeigen
          "</span>";
        cardHtml += "</a>";

        cardHtml += "</div>";
        cardHtml += "</div>";
        cardHtml += "</div>";
        cardHtml += "</div>";

        return cardHtml;
      } catch (error) {
        return null;
      }
    });

    const cards = await Promise.all(productCardPromises);
    return cards.filter((card) => card !== null);
  }

  renderProducts(productCards, boostData, containerAttrs = {}) {
    const productsList = document.getElementById("ProductsList");
    if (!productsList) {
      return;
    }

    // Remove loading state if still present
    this.removeLoadingState();

    // Clear existing content
    productsList.innerHTML = "";

    // Append each card as separate element for proper grid structure
    productCards.forEach((cardHtml) => {
      if (cardHtml) {
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = cardHtml.trim();
        const cardElement = tempDiv.firstElementChild;
        if (cardElement) {
          productsList.appendChild(cardElement);
        }
      }
    });

    // Restore grid classes and styles
    if (containerAttrs.originalClasses) {
      productsList.className = containerAttrs.originalClasses;
    }
    if (containerAttrs.originalStyle) {
      productsList.setAttribute("style", containerAttrs.originalStyle);
    }
    if (containerAttrs.originalDataAttrs) {
      if (containerAttrs.originalDataAttrs.gridClass) {
        productsList.setAttribute(
          "data-grid-class",
          containerAttrs.originalDataAttrs.gridClass,
        );
      }
      if (containerAttrs.originalDataAttrs.listClass) {
        productsList.setAttribute(
          "data-list-class",
          containerAttrs.originalDataAttrs.listClass,
        );
      }
    }

    // Update pagination
    this.updatePagination(boostData);

    this.setToolbarAndFiltersVisibility(true);

    // Load Judge.me star rating badges from cache server
    if (window.JudgeMeForSearch?.loadBadges) {
      JudgeMeForSearch.loadBadges(productsList).catch(() => { });
    }

    // Restore full opacity and hide loading spinner
    productsList.style.opacity = "1";
    this.setFacetLoading(false);
  }

  setToolbarAndFiltersVisibility(visible) {
    const display = visible ? "" : "none";
    const toolbarWrapper = document
      .querySelector(".collection__toolbar")
      ?.closest(".sticky-element");
    if (toolbarWrapper) toolbarWrapper.style.display = display;
    const facetsActive = document.getElementById("FacetFiltersActive");
    if (facetsActive) facetsActive.style.display = display;
    const facetFiltersContainer = document.getElementById(
      "FacetFiltersContainer",
    );
    if (facetFiltersContainer) facetFiltersContainer.style.display = display;
  }

  updatePagination(boostData) {
    const totalProducts = boostData.total_product;
    const totalPages = Math.ceil(totalProducts / this.productsPerPage);

    // Find or create pagination container
    let paginationContainer = document.querySelector(".pagination");
    const productGridContainer = document.getElementById(
      "ProductGridContainer",
    );

    if (!paginationContainer && productGridContainer) {
      // Create pagination container if it doesn't exist
      paginationContainer = document.createElement("nav");
      paginationContainer.className = "pagination";
      paginationContainer.setAttribute("role", "navigation");
      paginationContainer.setAttribute("aria-label", "Pagination");
      productGridContainer.appendChild(paginationContainer);
    }

    if (!paginationContainer || totalPages <= 1) {
      if (paginationContainer) {
        paginationContainer.style.display = "none";
      }
      return;
    }

    paginationContainer.style.display = "block";

    const strings = window.searchPaginationStrings || {};
    const previousLabel = strings.previous || "Previous";
    const nextLabel = strings.next || "Next";
    const pageLabel = (num) =>
      (strings.page || "Page __number__").replace("__number__", num);

    // Build pagination HTML
    let paginationHtml =
      '<ul class="pagination__list list-unstyled flex items-center flex-wrap justify-center" role="list">';

    // Previous button
    if (this.currentPage > 1) {
      const prevUrl = this.buildPaginationURL(this.currentPage - 1);
      paginationHtml +=
        '<li><a href="' +
        prevUrl +
        '" class="pagination__item pagination__item--previous pagination__item-arrow motion-reduce">';
      paginationHtml +=
        '<svg class="icon icon-caret-left rtl-flip-x icon--extra-small icon--thick" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12.5 16.25L6.25 10L12.5 3.75" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      paginationHtml += "<span>" + previousLabel + "</span></a></li>";
    }

    // Show first, last, current, and 2 pages around current
    const pagesToShow = new Set();
    pagesToShow.add(1);
    pagesToShow.add(totalPages);
    for (
      let i = Math.max(1, this.currentPage - 2);
      i <= Math.min(totalPages, this.currentPage + 2);
      i++
    ) {
      pagesToShow.add(i);
    }

    let lastShown = 0;
    const sortedPages = Array.from(pagesToShow).sort((a, b) => a - b);

    sortedPages.forEach((pageNum) => {
      if (pageNum > lastShown + 1) {
        paginationHtml += '<li><span class="pagination__item">...</span></li>';
      }

      const pageUrl = this.buildPaginationURL(pageNum);
      if (pageNum === this.currentPage) {
        paginationHtml +=
          '<li><a role="link" aria-disabled="true" class="pagination__item pagination__item--current background-2" aria-current="page" aria-label="' +
          pageLabel(pageNum) +
          '">' +
          pageNum +
          "</a></li>";
      } else {
        paginationHtml +=
          '<li><a href="' +
          pageUrl +
          '" class="pagination__item" aria-label="' +
          pageLabel(pageNum) +
          '">' +
          pageNum +
          "</a></li>";
      }
      lastShown = pageNum;
    });

    // Next button
    if (this.currentPage < totalPages) {
      const nextUrl = this.buildPaginationURL(this.currentPage + 1);
      paginationHtml +=
        '<li><a href="' +
        nextUrl +
        '" class="pagination__item pagination__item--next pagination__item-arrow motion-reduce">';
      paginationHtml += "<span>" + nextLabel + "</span>";
      paginationHtml +=
        '<svg class="icon icon-caret-right rtl-flip-x icon--extra-small icon--thick" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7.5 3.75L13.75 10L7.5 16.25" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      paginationHtml += "</a></li>";
    }

    paginationHtml += "</ul>";
    paginationContainer.innerHTML = paginationHtml;
  }

  buildPaginationURL(page) {
    const url = new URL(window.location.href);
    url.searchParams.set("q", this.currentQuery);
    url.searchParams.set("page", page.toString());
    if (this.currentProductType) {
      url.searchParams.set("product_type", this.currentProductType);
    } else {
      url.searchParams.delete("product_type");
    }
    if (this.currentSort) {
      url.searchParams.set("sort_by", this.currentSort);
    } else {
      url.searchParams.delete("sort_by");
    }
    url.searchParams.delete("section_id");
    return url.toString();
  }

  updateURL() {
    const newUrl = this.buildPaginationURL(this.currentPage);
    window.history.pushState(
      { page: this.currentPage, query: this.currentQuery },
      "",
      newUrl,
    );
  }

  updateProductCount(count) {
    const productCountEl = document.getElementById("ProductCount");
    if (count === undefined || count === null) {
      return;
    }

    const resultText =
      count === 1
        ? FoxTheme.searchStrings?.resultsWithCountOne // {{ count }} Ergebnis
        : FoxTheme.searchStrings?.resultsWithCountOther; // {{ count }} Ergebnisse
    if (!resultText) {
      return;
    }

    const countText = resultText
      .replace(/\{\{ count \}\}/g, count.toString())
      .replace(/\[count\]/g, count.toString());
    if (productCountEl) {
      productCountEl.textContent = countText;
    }
    // Update tab title with Boost AI result count
    this.updatePageTitle(count);
  }

  // Set document.title to "Suche: X Ergebnisse gefunden für 'Y' – Shop Name"
  updatePageTitle(count) {
    if (count === undefined || count === null) {
      return;
    }
    const template = FoxTheme.searchStrings?.resultsWithCountAndTerm;
    if (!template) {
      return;
    }
    const terms = this.currentQuery || "";
    const titlePart = template
      .replace(/\{\{\s*count\s*\}\}/g, count.toString())
      .replace(/\[count\]/g, count.toString())
      .replace(/\{\{\s*terms\s*\}\}/g, terms)
      .replace(/\[terms\]/g, terms);
    const shopName = FoxTheme.shopName || "";
    document.title = shopName ? `${titlePart} – ${shopName}` : titlePart;
  }

  getActiveFacetsSignature() {
    const params = new URLSearchParams(window.location.search);
    params.delete("page");
    params.delete("section_id");
    return params.toString();
  }

  async refreshActiveFacets(customUrl = null) {
    const target = document.getElementById("FacetFiltersActive");
    if (!target) {
      return;
    }

    // Try Shopify's response first (works for standard filters)
    const form = document.querySelector('form[is="facet-form"]');
    const sectionId =
      form && FoxTheme?.utils?.getSectionId
        ? FoxTheme.utils.getSectionId(form)
        : null;
    if (!sectionId) {
      return;
    }

    const url = customUrl ? new URL(customUrl) : new URL(window.location.href);
    url.searchParams.set("section_id", sectionId);

    try {
      const responseText = await fetch(url.toString()).then((r) => r.text());
      const parsedHTML = new DOMParser().parseFromString(
        responseText,
        "text/html",
      );
      const source = parsedHTML.getElementById("FacetFiltersActive");

      if (source && source.innerHTML.trim()) {
        // Shopify returned chips, use them
        target.innerHTML = source.innerHTML;
        if (window.customElements?.upgrade) {
          target.querySelectorAll('[is="facet-remove"]').forEach((el) => {
            window.customElements.upgrade(el);
          });
        }
      } else {
        // Shopify didn't return chips (custom metafield filters), build them manually
        const chipsHtml = this.buildActiveFacetsFromURL(url);
        target.innerHTML = chipsHtml;
        if (window.customElements?.upgrade) {
          target.querySelectorAll('[is="facet-remove"]').forEach((el) => {
            window.customElements.upgrade(el);
          });
        }
      }
    } catch (e) {
      // Fallback to manual building
      const chipsHtml = this.buildActiveFacetsFromURL(url);
      target.innerHTML = chipsHtml;
    }
  }

  buildActiveFacetsFromURL(url) {
    const params = new URLSearchParams(url.search);
    const skipKeys = new Set([
      "q",
      "page",
      "sort_by",
      "product_type",
      "section_id",
    ]);
    const chips = [];
    const filterTree = this.filterTree;
    const filterOptions = filterTree?.options || [];

    // Price filter
    const priceGte = params.get("filter.v.price.gte");
    const priceLte = params.get("filter.v.price.lte");
    if (priceGte || priceLte) {
      const priceFilter = filterOptions.find(
        (opt) =>
          opt.filterType === "price" || opt.filterOptionId === "pf_p_price",
      );
      // Form inputs submit display units (e.g. 1606, 3500). Shopify/URL can use cents (160600). Use magnitude to detect.
      const gteNum = priceGte ? parseFloat(priceGte) : NaN;
      const lteNum = priceLte ? parseFloat(priceLte) : NaN;
      const toCents = (v) =>
        !isNaN(v) && v > 10000 ? v : Math.round((v || 0) * 100);
      const minCents = priceGte ? toCents(gteNum) : 0;
      const rawMaxFromTree = priceFilter?.values?.max ?? priceFilter?.values?.max_value;
      const maxCents =
        priceLte && !isNaN(lteNum)
          ? toCents(lteNum)
          : rawMaxFromTree != null
            (rawMaxFromTree > 10000 ? rawMaxFromTree : Math.round(Number(rawMaxFromTree) * 100))

      const removeUrl = new URL(
        window.location.pathname,
        window.location.origin,
      );
      params.forEach((value, key) => {
        if (
          key !== "filter.v.price.gte" &&
          key !== "filter.v.price.lte" &&
          !skipKeys.has(key)
        ) {
          removeUrl.searchParams.append(key, value);
        }
      });
      if (params.get("q")) {
        removeUrl.searchParams.set("q", params.get("q"));
      }
      if (params.get("sort_by")) {
        removeUrl.searchParams.set("sort_by", params.get("sort_by"));
      }

      const moneyFormat = FoxTheme?.settings?.moneyFormat || FoxTheme?.Currency?.moneyFormat;
      const formatMoney = (amountInCents) =>
        typeof FoxTheme?.Currency?.formatMoney === "function"
          ? FoxTheme.Currency.formatMoney(amountInCents, moneyFormat)
          : String(amountInCents / 100);
      const label = `${formatMoney(minCents)} - ${formatMoney(maxCents)}`;
      chips.push(this.createFacetChip(label, removeUrl.toString()));
    }

    // Other filters
    const filterGroups = new Map();
    params.forEach((value, key) => {
      if (skipKeys.has(key) || key.startsWith("filter.v.price.")) {
        return;
      }
      if (!key.startsWith("filter.") && !key.startsWith("pf_")) {
        return;
      }

      // Group by filter key (handle multiple values)
      if (!filterGroups.has(key)) {
        filterGroups.set(key, []);
      }
      filterGroups.get(key).push(value);
    });

    filterGroups.forEach((values, key) => {
      // Find matching filter option in tree
      const filterOption = filterOptions.find((opt) => {
        if (key.startsWith("pf_")) {
          return opt.filterOptionId === key;
        }
        // Match custom metafield filters: filter.p.m.custom.content -> metafield_::custom::content
        if (key.startsWith("filter.p.m.custom.")) {
          const metafieldName = key.replace("filter.p.m.custom.", "");
          return opt.filterType === `metafield_::custom::${metafieldName}`;
        }
        return false;
      });

      values.forEach((value) => {
        // Find label from filter tree
        let label = value;
        if (filterOption?.values) {
          const valueObj = Array.isArray(filterOption.values)
            ? filterOption.values.find((v) => {
              const valKey = typeof v === "object" ? v.key : v;
              return valKey === value || valKey === decodeURIComponent(value);
            })
            : null;
          if (valueObj && typeof valueObj === "object" && valueObj.key) {
            label = valueObj.key;
          }
        }

        // Build remove URL (remove this specific value)
        const removeUrl = new URL(
          window.location.pathname,
          window.location.origin,
        );
        // Add query and sort first
        if (params.get("q")) {
          removeUrl.searchParams.set("q", params.get("q"));
        }
        if (params.get("sort_by")) {
          removeUrl.searchParams.set("sort_by", params.get("sort_by"));
        }

        // Add all other filter params except this specific value
        params.forEach((v, k) => {
          if (skipKeys.has(k)) {
            return;
          }
          if (!k.startsWith("filter.") && !k.startsWith("pf_")) {
            return;
          }

          // For the same key, only add values that aren't the one we're removing
          if (k === key) {
            if (v !== value) {
              removeUrl.searchParams.append(k, v);
            }
          } else {
            // Different key, add all values
            removeUrl.searchParams.append(k, v);
          }
        });

        chips.push(this.createFacetChip(label, removeUrl.toString()));
      });
    });

    // Clear all link (if multiple filters)
    if (chips.length > 1) {
      const clearAllUrl = new URL(
        window.location.pathname,
        window.location.origin,
      );
      if (params.get("q")) {
        clearAllUrl.searchParams.set("q", params.get("q"));
      }
      if (params.get("sort_by")) {
        clearAllUrl.searchParams.set("sort_by", params.get("sort_by"));
      }
      clearAllUrl.searchParams.set("options[prefix]", "last");
      clearAllUrl.searchParams.set("type", "product");
      chips.push(
        `<a href="${clearAllUrl.toString()}" class="facet-clear-all btn--link" is="facet-remove">Alles löschen</a>`,
      );
    }

    return chips.join("");
  }

  createFacetChip(label, removeUrl) {
    return `<a class="facet-remove btn btn--secondary flex relative" href="${SearchUtils.escapeHtml(removeUrl)}" aria-label="Löschen" is="facet-remove">
      <div class="btn__text flex items-center justify-between gap-2">
        <div class="facet-title text-wrap flex items-center gap-2">${SearchUtils.escapeHtml(label)}</div>
        <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="icon icon-close icon--extra-small icon--thick" fill="none" viewBox="0 0 24 24">
          <path d="M18.75 5.13496L5.25 18.8544" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
          <path d="M18.75 18.8544L5.25 5.13496" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
        </svg>
      </div>
    </a>`;
  }

  getAppliedFilterCount(urlParams) {
    const params = urlParams || new URLSearchParams(window.location.search);
    const skipKeys = new Set([
      "q",
      "page",
      "sort_by",
      "product_type",
      "section_id",
    ]);
    let count = 0;
    let hasPrice = false;

    params.forEach((value, key) => {
      if (skipKeys.has(key)) {
        return;
      }
      if (!value) {
        return;
      }

      if (key === "filter.v.price.gte" || key === "filter.v.price.lte") {
        if (!hasPrice) {
          hasPrice = true;
          count += 1;
        }
        return;
      }

      if (key.startsWith("filter.") || key.startsWith("pf_")) {
        count += 1;
      }
    });

    return count;
  }

  setupActiveFacetsObserver() {
    const target = document.getElementById("FacetFiltersActive");
    if (!target) {
      // Wait for element to appear
      const observer = new MutationObserver(() => {
        const el = document.getElementById("FacetFiltersActive");
        if (el) {
          observer.disconnect();
          this.setupActiveFacetsObserver();
        }
      });
      observer.observe(document.body, { childList: true, subtree: true });
      return;
    }

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === "childList") {
          // Monitor for unexpected changes (no action needed, just observing)
        }
      });
    });

    observer.observe(target, {
      childList: true,
      subtree: true,
      attributes: false,
    });
  }

  overrideFacetCountBehavior() {
    // Override FacetCount's onFacetUpdate to prevent it from clearing on search pages
    const overrideFacetCount = () => {
      const facetCounts = document.querySelectorAll("facet-count");
      facetCounts.forEach((facetCount) => {
        if (facetCount._boostAIFacetCountOverridden) {
          return;
        }
        facetCount._boostAIFacetCountOverridden = true;

        // Store original onFacetUpdate
        const originalOnFacetUpdate =
          facetCount.onFacetUpdate?.bind(facetCount);

        // Override onFacetUpdate to skip updates on search pages
        facetCount.onFacetUpdate = (event) => {
          // On search pages, don't let FacetCount update from Shopify's response
          if (
            window.location.pathname === "/search" ||
            window.location.pathname.includes("/search")
          ) {
            // Don't update from Shopify's response, keep our count
            return;
          } else {
            // Not on search page, use original behavior
            if (originalOnFacetUpdate) {
              originalOnFacetUpdate(event);
            }
          }
        };
      });
    };

    // Run immediately and on DOMContentLoaded
    overrideFacetCount();
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", overrideFacetCount);
    }

    // Also use MutationObserver to handle dynamically added facet-count elements
    const observer = new MutationObserver(overrideFacetCount);
    observer.observe(document.body, { childList: true, subtree: true });
  }

  setupFacetCountObserver() {
    // Watch for changes to filter buttons and re-apply count
    const observer = new MutationObserver(() => {
      // Debounce updates to avoid excessive calls
      if (this._facetCountUpdateTimeout) {
        clearTimeout(this._facetCountUpdateTimeout);
      }
      this._facetCountUpdateTimeout = setTimeout(() => {
        this.updateAppliedFilterCount();
      }, 100);
    });

    // Observe the facet drawer wrapper for changes
    const facetWrapper = document.querySelector(".facet-drawer-wrapper");
    if (facetWrapper) {
      observer.observe(facetWrapper, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }
  }

  // Clear filter option counts so we don't show Shopify numbers before Boost AI loads
  clearFilterOptionCounts() {
    document.querySelectorAll("label .count").forEach((el) => {
      if (el && el.textContent !== "") el.textContent = "";
    });
  }

  // Update filter option counts from Boost AI filter tree (doc_count).
  // Find labels by for="vertical-filter.p.m.custom.content-1" (desktop) and "drawer-..." (mobile).
  updateFilterOptionCountsFromBoostAI(filterTree) {
    if (!filterTree?.options?.length) {
      return;
    }

    const normalize = (str) =>
      String(str ?? "")
        .trim()
        .toLowerCase();

    const contextPrefixes = ["vertical", "drawer"];

    filterTree.options.forEach((filterOption) => {
      if (!filterOption.values?.length) {
        return;
      }
      if (
        filterOption.filterType === "price" ||
        filterOption.filterOptionId === "pf_p_price"
      ) {
        return;
      }

      let paramName = null;
      if (filterOption.filterType?.startsWith("metafield_::custom::")) {
        paramName =
          "filter.p.m.custom." +
          filterOption.filterType.replace("metafield_::custom::", "");
      } else if (filterOption.filterOptionId?.startsWith("pf_")) {
        paramName = filterOption.filterOptionId;
      }
      if (!paramName) {
        return;
      }

      // Find filter groups for both desktop (vertical) and mobile (drawer)
      contextPrefixes.forEach((prefix) => {
        const firstLabel = document.querySelector(
          `label[for*="${prefix}-${paramName}"]`,
        );
        const filterGroup = firstLabel?.closest("details");
        if (!filterGroup) {
          return;
        }

        const labelsWithCount = filterGroup.querySelectorAll("label .count");
        const valueLabels = Array.from(labelsWithCount)
          .map((c) => c.closest("label")?.querySelector(".reversed-link__text"))
          .filter(Boolean);

        filterOption.values.forEach((val) => {
          const docCount =
            typeof val.doc_count !== "undefined" ? val.doc_count : val.count;
          if (docCount === undefined) {
            return;
          }

          const keyStr = String(val.key ?? val.label ?? val).trim();
          const labelStr = String(val.label ?? val.key ?? val).trim();
          const keyNorm = normalize(keyStr);
          const labelNorm = normalize(labelStr);

          const textSpan = Array.from(valueLabels).find((span) => {
            const text = (span?.textContent ?? "").trim();
            return (
              normalize(text) === keyNorm ||
              normalize(text) === labelNorm ||
              text === keyStr ||
              text === labelStr
            );
          });

          const label = textSpan?.closest("label");
          const countSpan = label?.querySelector(".count");
          if (countSpan) countSpan.textContent = docCount;
        });
      });
    });
  }

  updateAppliedFilterCount(urlParams) {
    const count = this.getAppliedFilterCount(urlParams);

    // Find all filter buttons that should have count badge
    const filterButtons = document.querySelectorAll(
      ".facet-drawer-button .btn__text, .drawer__sticky .btn .btn__text",
    );

    filterButtons.forEach((btnText) => {
      // Remove any existing facet-count (native) or boost-ai-facet-count (ours)
      const existingBadge = btnText.querySelector(
        "facet-count, boost-ai-facet-count",
      );
      if (existingBadge) {
        existingBadge.remove();
      }

      // Create our own custom element that won't be affected by FacetCount
      if (count > 0) {
        const badge = document.createElement("boost-ai-facet-count");
        badge.textContent = `(${count})`;
        btnText.appendChild(badge);
      }
    });

    // Also update with a delay to catch any late updates from native code
    setTimeout(() => {
      const filterButtonsDelayed = document.querySelectorAll(
        ".facet-drawer-button .btn__text, .drawer__sticky .btn .btn__text",
      );
      filterButtonsDelayed.forEach((btnText) => {
        const existingBadge = btnText.querySelector(
          "facet-count, boost-ai-facet-count",
        );
        if (existingBadge) {
          existingBadge.remove();
        }
        if (count > 0) {
          const badge = document.createElement("boost-ai-facet-count");
          badge.textContent = `(${count})`;
          btnText.appendChild(badge);
        }
      });
    }, 300);
  }

  renderEmptyResults() {
    const productsList = document.getElementById("ProductsList");
    if (productsList) {
      const terms = this.currentQuery || "";
      const noResultsText = (FoxTheme.searchStrings?.noResultsText)
        .replace(/\{\{\s*terms\s*\}\}/g, terms)
        .replace(/\[terms\]/g, terms); // Für „[terms]“ wurden keine Ergebnisse gefunden.
      const noResultsMessage = FoxTheme.searchStrings?.noResultsMessage;
      productsList.innerHTML =
        "<div class='col-span-full w-full text-center grid gap-1 justify-items-center'><h4>" +
        SearchUtils.escapeHtml(noResultsText) + // Für „[terms]“ wurden keine Ergebnisse gefunden.
        "</h4><p class='m-0'>" +
        SearchUtils.escapeHtml(noResultsMessage) + // Überprüfen Sie die Rechtschreibung oder verwenden Sie ein anderes Wort oder eine andere Phrase.
        "</p></div>";
    }
  }

  submitForm() {
    // Fallback to default form submission
    const searchForm = document.querySelector('form[action*="search"]');
    if (searchForm) {
      searchForm.submit();
    }
  }

  loadMoreProducts() {
    // For load-more pagination type
    this.performSearch();
  }

  getSectionId() {
    // Try to get section ID from DOM or generate one
    const section = document.querySelector(".template-search");
    if (section && section.dataset.sectionId) {
      return section.dataset.sectionId;
    }
    return "main-search";
  }
}

// Custom element for Boost AI filter count (won't be affected by FacetCount)
class BoostAIFacetCount extends HTMLElement {
  constructor() {
    super();
  }
}
customElements.define("boost-ai-facet-count", BoostAIFacetCount);

// Initialize when DOM is ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    new MainSearch();
  });
} else {
  new MainSearch();
}
